<template>
  <v-row>
    <v-col cols="12">
      <gg-card>
        <gg-card-header>
          Create Staging Environment
        </gg-card-header>
        <gg-card-text>
          <v-data-table :headers="headers" :items="availableInstallations.clearList" :loading="loadingInstallations">
            <template v-slot:item.softurl="{ item }">
              <a :href="item.softurl" target="_blank">{{ item.softurl }}</a>
            </template>

            <template v-slot:item.actions="{ item }">
              <v-menu bottom left offset-x min-width="180">
                <template v-slot:activator="{ on, attrs }">
                  <gg-btn v-on="on" v-bind="attrs">
                    Manage
                    <v-icon class="ml-1">mdi-chevron-down</v-icon>
                  </gg-btn>
                </template>
                <v-list>
                  <v-list-item @click="openStageModal(item)">
                    <v-list-item-title>
                      Create Staging
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="openRemoveModal(item)">
                    <v-list-item-title>
                      Remove
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>
          </v-data-table>
        </gg-card-text>
      </gg-card>
    </v-col>

    <v-col cols="12">
      <gg-card>
        <gg-card-header>
          Manage Staging Environment
        </gg-card-header>
        <gg-card-text>
          <v-data-table :headers="stagingHeaders" :items="availableInstallations.stagingList" :loading="loadingInstallations">
            <template v-slot:item.softurl="{ item }">
              <a :href="item.softurl" target="_blank">{{ item.softurl }}</a>
            </template>

            <template v-slot:item.actions="{ item }">
              <gg-btn secondary @click="goLive(item)" :disabled="loadingPtl" :loading="loadingPtl && item.insid === selectedService.insid" class="mr-2">
                Push To Live
              </gg-btn>
              <v-menu bottom left offset-x min-width="180">
                <template v-slot:activator="{ on, attrs }">
                  <gg-btn v-on="on" v-bind="attrs">
                    Manage
                    <v-icon class="ml-1">mdi-chevron-down</v-icon>
                  </gg-btn>
                </template>
                <v-list>
                  <v-list-item @click="login(item)" :disabled="loadingLogin">
                    <v-list-item-title>
                      WP Dashboard
                      <v-icon small class='mb-1 ml-1'>mdi-open-in-new</v-icon>
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="openRemoveModal(item)">
                    <v-list-item-title>
                      Remove
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>
          </v-data-table>
        </gg-card-text>
      </gg-card>
    </v-col>

    <gg-modal :is-active="removeModal" v-if="removeModal" @close="closeRemoveModal">
      <template #toolbar>
        Remove Installation
      </template>
      <template #body>
        <v-row>
          <v-col cols="12">
            <v-checkbox label="Remove Directory" v-model="removeModel.remove_dir" true-value="on" false-value="off">
              <template v-slot:append>
                <v-tooltip
                  bottom
                  max-width="300"
                >
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on">
                      mdi-help-circle-outline
                    </v-icon>
                  </template>

                  If you check this the entire folder will be deleted
                </v-tooltip>
              </template>
            </v-checkbox>
          </v-col>
          <v-col cols="12">
            <v-checkbox label="Remove Database" v-model="removeModel.remove_db" true-value="on" false-value="off">
              <template v-slot:append>
                <v-tooltip
                  bottom
                  max-width="300"
                >
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on">
                      mdi-help-circle-outline
                    </v-icon>
                  </template>

                  If checked the database will be deleted
                </v-tooltip>
              </template>
            </v-checkbox>
          </v-col>
          <v-col cols="12">
            <v-checkbox label="Remove Database User" v-model="removeModel.remove_dbuser" true-value="on"
                        false-value="off">
              <template v-slot:append>
                <v-tooltip
                  bottom
                  max-width="300"
                >
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on">
                      mdi-help-circle-outline
                    </v-icon>
                  </template>

                  If checked the database user will also be deleted
                </v-tooltip>
              </template>
            </v-checkbox>
          </v-col>
        </v-row>
      </template>
      <template #actions>
        <gg-btn secondary @click="closeRemoveModal">Cancel</gg-btn>
        <gg-btn :loading="loadingRemove" :disabled="loadingRemove" @click="removeInstallation">Remove</gg-btn>
      </template>
    </gg-modal>
    <go-live-modal :options="pushToLiveOptions"
                   :service="selectedService"
                   :is-active="ptlModal"
                   v-if="ptlModal"
                   @update="getInstallations"
                   @close="closePtlModal"
    />
    <create-staging-modal :services="availableInstallations.clearList"
                          :service="selectedService"
                          :is-active="stageModal"
                          v-if="stageModal"
                          @close="closeStageModal"
    />

  </v-row>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import GoLiveModal from '@/components/Services/Hosting/Shared/Wordpress/Staging/GoLiveModal/GoLiveModal'
import { makeWpLoginUrl } from '@/helpers/wp'
import CreateStagingModal from '@/components/Services/Hosting/Shared/Wordpress/Staging/CreateStagingModal/CreateStagingModal'

export default {
  name: 'CreateEnvironment',
  components: {
    GoLiveModal,
    CreateStagingModal
  },
  data: () => ({
    loadingLogin: false,
    loadingInstallations: false,
    loadingStage: false,
    loadingRemove: false,
    loadingPtl: false,
    headers: [
      { text: 'Link', align: 'left', sortable: false, value: 'softurl' },
      { text: 'Version', align: 'left', sortable: false, value: 'ver' },
      { text: 'Actions', align: 'left', sortable: false, value: 'actions' },
    ],
    stagingHeaders: [
      { text: 'Name', align: 'left', sortable: false, value: 'site_name' },
      { text: 'Link', align: 'left', sortable: false, value: 'softurl' },
      { text: 'Version', align: 'left', sortable: false, value: 'ver' },
      { text: 'Actions', align: 'left', sortable: false, value: 'actions' },
    ],
    selectedService: {},
    model: {
      softdirectory: '',
      softdb: '',
      no_search_engine: 'off',
      site_name: '',
      softsubmit: 'Clone',
      stagingins: 'Create+Staging',
    },
    removeModel: {
      removeins: 'on',
      remove_dir: 'on',
      remove_db: 'on',
      remove_dbuser: 'on'

    },
    pushToLiveOptions: {
      structural_change_tables: [],
      datachange_tables: []
    },
    stageModal: false,
    removeModal: false,
    ptlModal: false,
  }),
  computed: {
    ...mapGetters({
      installations: 'hosting_wp/installations'
    }),
    availableInstallations () {
      const clearList = this.installations.filter(i => i.is_staging === undefined)
      const stagingList = this.installations.filter(i => i.is_staging !== undefined)

      return { clearList, stagingList }
    }
  },
  async mounted () {
    await this.getInstallations()
  },
  methods: {
    ...mapActions({
      getInstallationsList: 'hosting_wp/getInstallationsList',
      setStaging: 'hosting_wp/setStaging',
      removeScript: 'hosting_wp/removeScript',
      pushToLive: 'hosting_wp/pushToLive',
      reportSuccess: 'reportSuccess',
      reportError: 'reportError'
    }),
    async getInstallations () {
      try {
        this.loadingInstallations = true
        await this.getInstallationsList(this.$route.query.user)
      } catch (e) {
        this.reportError(e)
      } finally {
        this.loadingInstallations = false
      }
    },
    async removeInstallation () {
      try {
        this.loadingRemove = true

        await this.removeScript({
          insid: this.selectedService.insid,
          _username: this.$route.query.user,
          payload: {
            ...this.removeModel
          }
        })

        await this.getInstallationsList(this.$route.query.user)
        this.closeRemoveModal()
        this.reportSuccess('The installation was removed successfully')
      } catch (e) {
        this.reportError(e)
      } finally {
        this.loadingRemove = false
      }
    },
    async login ({ insid }) {
      this.loadingLogin = true
      try {
        const loginUrl = await makeWpLoginUrl({ insid, username: this.$route.query.user })
        window.open(loginUrl, '_blank').focus()
      } catch (e) {
        if (e.message.toString().includes('window.open')) {
          this.reportError('Please, allow the pop-up windows to open in your browser settings.')
        } else {
          this.reportError(e)
        }
      } finally {
        this.loadingLogin = false
      }
    },
    async goLive (item) {
      this.selectedService = item

      try {
        this.loadingPtl = true

        const { structural_change_tables, datachange_tables } = await this.pushToLive({
          insid: this.selectedService.insid,
          _username: this.$route.query.user,
        })

        this.pushToLiveOptions.structural_change_tables = structural_change_tables
        this.pushToLiveOptions.datachange_tables = datachange_tables

        this.openPtlModal()
      } catch (e) {
        this.reportError(e)
      } finally {
        this.loadingPtl = false
      }
    },
    openStageModal (item) {
      this.selectedService = item

      this.$nextTick(() => {
        this.stageModal = true
      })
    },
    closeStageModal () {
      this.stageModal = false

      this.model.softdirectory = ''
      this.model.softdb = ''
      this.model.no_search_engine = 'off'
      this.model.site_name = ''

      this.selectedService = null
    },
    openRemoveModal (item) {
      this.selectedService = item

      this.removeModal = true
    },
    closeRemoveModal () {
      this.removeModal = false

      this.model.remove_dir = 'on'
      this.model.remove_db = 'on'
      this.model.remove_dbuser = 'on'

      this.selectedService = null
    },
    openPtlModal () {
      this.ptlModal = true
    },
    closePtlModal () {
      this.ptlModal = false

      this.selectedService = null
    }
  }
}
</script>
